// The admin frame: left rail with the sections, top bar with one search box, and the page.
// On a phone the rail becomes a scrolling tab strip.
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'

import { getOverview, onSessionEnded, search as searchApi } from '../../api/adminV2'
import { endAdminSession, getAdminEmail, SESSION_ENDED_MESSAGE } from './session'
import { useFeedback } from './ui'

const SECTIONS = [
  { key: 'today', label: 'Today', to: '/admin/today' },
  { key: 'people', label: 'People', to: '/admin/people' },
  {
    key: 'second-accounts',
    label: 'Second accounts',
    to: '/admin/second-accounts',
    count: 'second',
  },
  { key: 'groups', label: 'Family groups', to: '/admin/groups' },
  { key: 'recipes', label: 'Recipes', to: '/admin/recipes', count: 'flagged' },
  {
    key: 'cookbook-orders',
    label: 'Cookbook orders',
    to: '/admin/cookbook-orders',
    count: 'orders',
  },
  { key: 'cookbook-journeys', label: 'Cookbook journeys', to: '/admin/cookbook-journeys' },
  { key: 'site', label: 'Site content', to: '/admin/site' },
]

const CountsContext = createContext({ counts: {}, refreshCounts: () => {} })
export const useCounts = () => useContext(CountsContext)

const countsFrom = (overview) => {
  const items = overview?.attention || []
  return {
    flagged: items.filter((i) => i.kind === 'flagged_recipe').length,
    orders: items.filter((i) => ['cookbook_order', 'cookbook_request'].includes(i.kind)).length,
    second: items.find((i) => i.kind === 'second_accounts')?.count || 0,
  }
}

const SearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="7" fill="none" />
    <path d="M20 20l-4-4" />
  </svg>
)

const TopSearch = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { toastError } = useFeedback()
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [hits, setHits] = useState(null)
  const [active, setActive] = useState(0)
  const boxRef = useRef(null)
  const seq = useRef(0)

  useEffect(() => {
    setOpen(false)
  }, [location.pathname])

  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) {
      setHits(null)
      return undefined
    }
    const mine = ++seq.current
    const timer = setTimeout(() => {
      searchApi(term)
        .then((data) => {
          if (mine !== seq.current) return
          const list = [
            ...(data?.people || []).map((p) => ({
              kind: 'Person',
              title: p.fullname || p.email,
              detail: p.email,
              to: `/admin/people/${p.id}`,
            })),
            ...(data?.groups || []).map((g) => ({
              kind: 'Group',
              title: g.name,
              detail: `${Number(g.member_count) || 0} members`,
              to: `/admin/groups/${g.id}`,
            })),
            ...(data?.recipes || []).map((r) => ({
              kind: 'Recipe',
              title: r.name || 'Untitled recipe',
              detail: r.author_name ? `by ${r.author_name}` : '',
              to: `/admin/recipes/${r.id}`,
            })),
          ]
          setHits(list)
          setActive(0)
          setOpen(true)
        })
        .catch((e) => {
          if (mine !== seq.current) return
          setHits([])
          if (e?.response?.status !== 401) toastError(e, 'Search did not work. Please try again.')
        })
    }, 250)
    return () => clearTimeout(timer)
  }, [q, toastError])

  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  const go = (hit) => {
    setOpen(false)
    setQ('')
    navigate(hit.to)
  }

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false)
      return
    }
    if (!hits?.length) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActive((a) => (a + 1) % hits.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => (a - 1 + hits.length) % hits.length)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      go(hits[active] || hits[0])
    }
  }

  const term = q.trim()
  return (
    <div className="a-search" ref={boxRef}>
      <SearchIcon />
      <input
        type="search"
        value={q}
        placeholder="Find a person, group or recipe by name or email"
        autoComplete="off"
        aria-label="Search everything"
        onChange={(e) => {
          setQ(e.target.value)
          setOpen(true)
        }}
        onFocus={() => hits && setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {open && term.length >= 2 && hits ? (
        <div className="a-results" role="listbox" aria-label="Search results">
          {hits.length ? (
            hits.map((hit, i) => (
              <button
                type="button"
                key={`${hit.to}`}
                data-active={i === active}
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(hit)}
              >
                <span className="a-kind">{hit.kind}</span>
                <span className="a-hit">
                  <b>{hit.title}</b> <span className="a-muted">{hit.detail}</span>
                </span>
              </button>
            ))
          ) : (
            <div className="a-muted" style={{ padding: 10 }}>
              Nothing found for "{term}".
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

const AdminLayout = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [counts, setCounts] = useState({})
  const email = getAdminEmail()

  const refreshCounts = useCallback(() => {
    getOverview()
      .then((data) => setCounts(countsFrom(data)))
      .catch(() => setCounts({}))
  }, [])

  useEffect(() => {
    refreshCounts()
  }, [refreshCounts])

  useEffect(
    () =>
      onSessionEnded(() => {
        endAdminSession(dispatch, SESSION_ENDED_MESSAGE)
        navigate('/admin', { replace: true })
      }),
    [dispatch, navigate]
  )

  const signOut = () => {
    endAdminSession(dispatch)
    navigate('/admin', { replace: true })
  }

  const section = location.pathname.split('/')[2] || 'today'

  return (
    <CountsContext.Provider value={{ counts, refreshCounts, setCounts }}>
      <div className="a-app">
        <aside className="a-rail">
          <div className="a-brand">
            <b>morsel</b>
            <span>admin</span>
          </div>
          <nav className="a-nav" aria-label="Admin sections">
            {SECTIONS.map((s) => (
              <Link key={s.key} to={s.to} aria-current={s.key === section ? 'page' : undefined}>
                {s.label}
                {s.count && counts[s.count] ? (
                  <span className="a-count">{counts[s.count]}</span>
                ) : null}
              </Link>
            ))}
          </nav>
          <div className="a-rail-foot">
            <span>{email ? `Signed in as ${email}` : 'Signed in'}</span>
            <button type="button" onClick={signOut}>
              Sign out
            </button>
          </div>
        </aside>
        <div className="a-main">
          <header className="a-topbar">
            <TopSearch />
            <button type="button" className="a-btn a-small a-topbar-out" onClick={signOut}>
              Sign out
            </button>
          </header>
          <main className="a-content">{children}</main>
        </div>
      </div>
    </CountsContext.Provider>
  )
}

export default AdminLayout
